import React, { useMemo, useState } from 'react';
import { X, TrendingUp, Users, PieChart, BarChart3, Euro, Calendar } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface BestandsModalProps {
  customers: any[];
  onClose: () => void;
}

export const BestandsModal: React.FC<BestandsModalProps> = ({ customers, onClose }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const parseMoney = (val: any): number => {
    if (val == null) return 0;
    let str = String(val).replace(/[€$EUR\s]/gi, '').trim();
    if (!str) return 0;
    if (str.includes('.') && str.includes(',')) {
      if (str.lastIndexOf(',') > str.lastIndexOf('.')) {
        str = str.replace(/\./g, '').replace(',', '.');
      } else {
        str = str.replace(/,/g, '');
      }
    } else if (str.includes(',')) {
      str = str.replace(',', '.');
    }
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  const formatEuro = (val: number) =>
    val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';

  // Filter-States
  const [selectedSparte, setSelectedSparte] = useState<string>('');
  const [minContracts, setMinContracts] = useState<string>('');
  const [maxContracts, setMaxContracts] = useState<string>('');

  // Alle Sparten extrahieren
  const allSparten = useMemo(() => {
    const spartenSet = new Set<string>();
    customers.forEach((c) => {
      (c.verträge || []).forEach((v: any) => {
        if (v.sparte) spartenSet.add(v.sparte);
      });
    });
    return Array.from(spartenSet).sort();
  }, [customers]);

  // Gefilterte Kundenliste
  const filteredCustomers = useMemo(() => {
    return customers.filter((c) => {
      const vertragsListe = (c.verträge || []);
      // Sparte-Filter
      const matchesSparte = selectedSparte
        ? vertragsListe.some((v: any) => v.sparte === selectedSparte)
        : true;
      // Vertragsanzahl-Filter
      const contractCount = vertragsListe.length;
      const minOk = minContracts ? contractCount >= parseInt(minContracts) : true;
      const maxOk = maxContracts ? contractCount <= parseInt(maxContracts) : true;
      return matchesSparte && minOk && maxOk;
    });
  }, [customers, selectedSparte, minContracts, maxContracts]);

  const stats = useMemo(() => {
    let totalBeitrag = 0;
    let totalMOB = 0;
    let contractsBySparte: Record<string, number> = {};
    let beitragBySparte: Record<string, number> = {};
    let zahlweiseCount: Record<string, number> = { monatlich: 0, vierteljährlich: 0, halbjährlich: 0, jährlich: 0 };
    let contractCount = 0;

    customers.forEach((c) => {
      (c.verträge || []).forEach((v: any) => {
        contractCount++;
        const beitrag = parseMoney(v.beitrag);
        const mob = parseMoney(v.mob);
        totalBeitrag += beitrag;
        totalMOB += mob;

        const sparte = v.sparte || 'Unbekannt';
        contractsBySparte[sparte] = (contractsBySparte[sparte] || 0) + 1;
        beitragBySparte[sparte] = (beitragBySparte[sparte] || 0) + beitrag;

        const zw = String(v.zahlweise || '').toLowerCase();
        if (zw.includes('monat')) zahlweiseCount.monatlich++;
        else if (zw.includes('vierteljähr') || zw.includes('quartal')) zahlweiseCount.vierteljährlich++;
        else if (zw.includes('halbjähr') || zw.includes('halb')) zahlweiseCount.halbjährlich++;
        else if (zw.includes('jähr') || zw.includes('jahr')) zahlweiseCount.jährlich++;
      });
    });

    const sortedSparten = Object.entries(beitragBySparte)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    return {
      totalBeitrag,
      totalMOB,
      contractCount,
      customerCount: customers.length,
      avgContractsPerCustomer: customers.length ? (contractCount / customers.length).toFixed(2) : '0',
      avgBeitragPerCustomer: customers.length ? totalBeitrag / customers.length : 0,
      contractsBySparte,
      sortedSparten,
      zahlweiseCount,
    };
  }, [customers]);

  return (
    <div className={`fixed inset-0 z-[70] flex items-center justify-center p-4 backdrop-blur-sm ${isDark ? 'bg-gray-900/60' : 'bg-slate-900/10'}`}>
      <div className={`rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col animate-scale-in border ${isDark ? 'glass-card border-white/20' : 'bg-white border-slate-200'}`}>
        <div className={`p-6 border-b flex justify-between items-center ${isDark ? 'glass-card bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-white/10' : 'bg-gradient-to-r from-cyan-50 to-blue-50 border-slate-200'}`}>
          <div>
            <h2 className={`text-3xl font-bold flex items-center gap-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <TrendingUp className={isDark ? 'text-cyan-400' : 'text-cyan-600'} size={32} />
              Bestandsauswertung
            </h2>
            <p className={`text-sm mt-1 ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Detaillierte Übersicht über Ihren Versicherungsbestand</p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-full shadow-sm transition-colors border ${isDark ? 'glass-card bg-white/10 hover:bg-white/20 border-white/20' : 'bg-slate-100 hover:bg-slate-200 border-slate-200'}`}
          >
            <X size={24} className={isDark ? 'text-white' : 'text-slate-900'} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Filter UI */}
          <div className="mb-8 flex flex-col md:flex-row gap-4 items-end">
            <div>
              <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-700'}`}>Sparte</label>
              <select
                className={`rounded-lg border px-3 py-2 w-48 ${isDark ? 'bg-slate-800 text-white border-white/20' : 'bg-white text-slate-900 border-slate-300'}`}
                value={selectedSparte}
                onChange={e => setSelectedSparte(e.target.value)}
              >
                <option value="">Alle Sparten</option>
                {allSparten.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-700'}`}>Min. Verträge/Kunde</label>
              <input
                type="number"
                min="0"
                className={`rounded-lg border px-3 py-2 w-32 ${isDark ? 'bg-slate-800 text-white border-white/20' : 'bg-white text-slate-900 border-slate-300'}`}
                value={minContracts}
                onChange={e => setMinContracts(e.target.value)}
                placeholder="z.B. 1"
              />
            </div>
            <div>
              <label className={`block text-xs font-semibold mb-1 ${isDark ? 'text-white/70' : 'text-slate-700'}`}>Max. Verträge/Kunde</label>
              <input
                type="number"
                min="0"
                className={`rounded-lg border px-3 py-2 w-32 ${isDark ? 'bg-slate-800 text-white border-white/20' : 'bg-white text-slate-900 border-slate-300'}`}
                value={maxContracts}
                onChange={e => setMaxContracts(e.target.value)}
                placeholder="z.B. 5"
              />
            </div>
            <div className="flex-1" />
            <div className="text-xs text-right text-slate-500 dark:text-white/50">
              {filteredCustomers.length} von {customers.length} Kunden angezeigt
            </div>
          </div>

          {/* Gefilterte Kundenliste */}
          <div className="mb-10">
            <h3 className={`text-lg font-bold mb-2 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <Users size={18} className={isDark ? 'text-blue-300' : 'text-blue-700'} />
              Gefilterte Kundenliste
            </h3>
            <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-white/10">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className={isDark ? 'bg-slate-800 text-white/80' : 'bg-slate-100 text-slate-700'}>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Verträge</th>
                    <th className="px-3 py-2 text-left">Sparten</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.length === 0 && (
                    <tr><td colSpan={3} className="px-3 py-4 text-center text-slate-400">Keine Kunden gefunden</td></tr>
                  )}
                  {filteredCustomers.map((c, idx) => (
                    <tr key={c.id || idx} className={isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-200/40'}>
                      <td className="px-3 py-2 font-medium">{c.name || c.vorname + ' ' + c.nachname}</td>
                      <td className="px-3 py-2">{(c.verträge || []).length}</td>
                      <td className="px-3 py-2">
                        {Array.from(new Set((c.verträge || []).map((v: any) => v.sparte))).filter(Boolean).join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {/* Hauptkennzahlen */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className={`p-6 rounded-2xl border ${isDark ? 'glass-card bg-gradient-to-br from-cyan-500/20 to-cyan-500/10 border-cyan-400/30' : 'bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'glass-card bg-cyan-500/30 border-cyan-400/50' : 'bg-cyan-200 border-cyan-300'}`}>
                  <Euro size={24} className={isDark ? 'text-cyan-300' : 'text-cyan-700'} />
                </div>
                <div>
                  <p className={`text-xs font-bold uppercase ${isDark ? 'text-cyan-300' : 'text-cyan-700'}`}>Gesamtbeitrag (Jahr)</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-cyan-200' : 'text-cyan-900'}`}>{formatEuro(stats.totalBeitrag)}</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-2xl border ${isDark ? 'glass-card bg-gradient-to-br from-green-500/20 to-green-500/10 border-green-400/30' : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'glass-card bg-green-500/30 border-green-400/50' : 'bg-green-200 border-green-300'}`}>
                  <TrendingUp size={24} className={isDark ? 'text-green-300' : 'text-green-700'} />
                </div>
                <div>
                  <p className={`text-xs font-bold uppercase ${isDark ? 'text-green-300' : 'text-green-700'}`}>Crosselling-Quote</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-green-200' : 'text-green-900'}`}>{stats.avgContractsPerCustomer}</p>
                  <p className={`text-[10px] font-semibold ${isDark ? 'text-green-300' : 'text-green-700'}`}>Ø Verträge/Kunde</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-2xl border ${isDark ? 'glass-card bg-gradient-to-br from-blue-500/20 to-blue-500/10 border-blue-400/30' : 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'glass-card bg-blue-500/30 border-blue-400/50' : 'bg-blue-200 border-blue-300'}`}>
                  <Users size={24} className={isDark ? 'text-blue-300' : 'text-blue-700'} />
                </div>
                <div>
                  <p className={`text-xs font-bold uppercase ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>Kunden</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-blue-200' : 'text-blue-900'}`}>{stats.customerCount}</p>
                </div>
              </div>
            </div>

            <div className={`p-6 rounded-2xl border ${isDark ? 'glass-card bg-gradient-to-br from-purple-500/20 to-purple-500/10 border-purple-400/30' : 'bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200'}`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center border ${isDark ? 'glass-card bg-purple-500/30 border-purple-400/50' : 'bg-purple-200 border-purple-300'}`}>
                  <BarChart3 size={24} className={isDark ? 'text-purple-300' : 'text-purple-700'} />
                </div>
                <div>
                  <p className={`text-xs font-bold uppercase ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>Verträge</p>
                  <p className={`text-2xl font-bold ${isDark ? 'text-purple-200' : 'text-purple-900'}`}>{stats.contractCount}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Top Sparten nach Beitrag */}
          <div className={`border rounded-2xl p-6 mb-6 ${isDark ? 'glass-card border-white/20' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <BarChart3 size={20} className={isDark ? 'text-rose-400' : 'text-rose-600'} />
              Top Sparten nach Beitrag
            </h3>
            <div className="space-y-3">
              {stats.sortedSparten.map(([sparte, beitrag], idx) => (
                <div key={sparte} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm border ${isDark ? 'glass-card bg-gradient-to-br from-rose-500/20 to-pink-500/20 text-white/80 border-rose-400/30' : 'bg-gradient-to-br from-rose-100 to-pink-100 text-rose-700 border-rose-300'}`}>
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{sparte}</span>
                      <span className={`font-bold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{formatEuro(beitrag)}</span>
                    </div>
                    <div className={`w-full rounded-full h-2 overflow-hidden border ${isDark ? 'bg-white/10 border-white/10' : 'bg-slate-200 border-slate-300'}`}>
                      <div
                        className={`h-full rounded-full transition-all border ${isDark ? 'glass-card bg-gradient-to-r from-rose-500/20 to-pink-500/20 border-rose-400/30' : 'bg-gradient-to-r from-rose-400 to-pink-400 border-rose-500'}`}
                        style={{
                          width: `${stats.totalBeitrag ? (beitrag / stats.totalBeitrag) * 100 : 0}%`,
                        }}
                      />
                    </div>
                    <div className={`flex justify-between text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                      <span>{stats.contractsBySparte[sparte]} Verträge</span>
                      <span>
                        {stats.totalBeitrag ? ((beitrag / stats.totalBeitrag) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Sparten nach Vertragszahl */}
          <div className={`border rounded-2xl p-6 ${isDark ? 'glass-card border-white/20' : 'bg-slate-50 border-slate-200'}`}>
            <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <BarChart3 size={20} className={isDark ? 'text-indigo-400' : 'text-indigo-600'} />
              Top Sparten nach Verträgen
            </h3>
            <div className="space-y-3">
              {Object.entries(stats.contractsBySparte)
                .sort((a, b) => (b[1] as number) - (a[1] as number))
                .slice(0, 10)
                .map(([sparte, count], idx) => (
                  <div key={sparte} className="flex items-center gap-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm border ${isDark ? 'glass-card bg-gradient-to-br from-indigo-500/20 to-purple-500/20 text-white/80 border-indigo-400/30' : 'bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-700 border-indigo-300'}`}>
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{sparte}</span>
                        <span className={`font-bold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>{count} Verträge</span>
                      </div>
                      <div className={`w-full rounded-full h-2 overflow-hidden border ${isDark ? 'bg-white/10 border-white/10' : 'bg-slate-200 border-slate-300'}`}>
                        <div
                          className={`h-full rounded-full transition-all border ${isDark ? 'glass-card bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border-indigo-400/30' : 'bg-gradient-to-r from-indigo-400 to-purple-400 border-indigo-500'}`}
                          style={{
                            width: `${stats.contractCount ? (count / stats.contractCount) * 100 : 0}%`,
                          }}
                        />
                      </div>
                      <div className={`flex justify-between text-xs mt-1 ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                        <span>{formatEuro(stats.sortedSparten.find(([s]) => s === sparte)?.[1] || 0)}</span>
                        <span>
                          {stats.contractCount ? ((count / stats.contractCount) * 100).toFixed(1) : 0}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
